import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  getAllCourses,
  getCategories,
  getLimitedCourses,
} from "../apiConfig/services";
import CardBookShimmer from "./loading/CardBookShimmer";
import { toast } from "react-toastify";

function CardBook() {
  const [books, setBooks] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isHomePage = location.pathname === "/home" || location.pathname === "/";
  const isLivresPage = location.pathname === "/livres";

  const fetchDomains = useCallback(async () => {
    try {
      const response = await getCategories();
      setDomains(response);
    } catch (error) {
      console.error("Erreur lors de la récupération des catégories", error);
      toast.error("Erreur lors de la récupération des catégories", {
        position: "bottom-right",
      });
    }
  }, []);

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const booksData = isHomePage
        ? await getLimitedCourses(6)
        : await getAllCourses();
      setBooks(booksData);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors de la récupération des livres", error);
      toast.error("Erreur lors du chargement des livres", {
        position: "bottom-right",
      });
      setLoading(false);
    }
  }, [isHomePage]);

  useEffect(() => {
    fetchBooks();
    fetchDomains();
  }, [fetchBooks, fetchDomains]);

  // Function to safely get category description
  const getCategoryDescription = (categoryName) => {
    if (!categoryName) return "";
    const domain = domains.find((d) => d.name_category?.includes(categoryName));
    return domain?.desc_category || "";
  };

  if (loading) {
    return <CardBookShimmer isLivresPage={isLivresPage} />;
  }

  return (
    <div className="container mx-auto mt-5">
      {/* Learning Paths Section - Only on Livres Page */}
      {isLivresPage && (
        <div className="w-full mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            {/* Beginner Path */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Parcours pour débutant{" "}
                <span className="text-sm text-gray-500">(env. 10h)</span>
              </h3>
              <div className="space-y-3">
                {[
                  {
                    text: "Vidéo onglet Documentaires généraux : « Télédetection radar du CNRS » (sous-titrée en malgache), puis « Pixels et paysages »",
                    category: "Documentaires généraux",
                  },
                  {
                    text: "Onglet Cours rédigés – Premier chapitre du cours",
                    category: "Cours rédigés",
                  },
                  {
                    text: "Onglet Plan résumé et liens vers le cours RADAR de l'ESA – Chapitre 1",
                    category: "RADAR de l'ESA",
                  },
                  {
                    text: "Onglet Exposés Power Point. Ecole d'été univ Fiana Avril 2024 – PowerPoint du premier jour",
                    category: "Fiana Avril 2024",
                  },
                  {
                    text: "Faire une synthèse des éléments principaux, rédiger 2 pages A4, échanger et discuter en groupe",
                    category: "",
                  },
                  {
                    text: "Consulter l'onglet Plans resumés et liens vers les cours RADAR, OPTIQUE RADAR, LIDAR de l'ENSG pour mots clefs et abréviations",
                    category: "ENSG",
                  },
                ].map((item, index) => (
                  <div
                    key={`beginner-${item.category || item.text.slice(0, 20)}`}
                    className="pl-4 border-l-2 border-blue-200"
                  >
                    <p className="text-gray-700">
                      <span className="font-semibold">{index + 1}.</span>{" "}
                      {item.text}
                    </p>
                    {item.category && (
                      <div
                        className="text-xs text-gray-500 mt-1 ml-5"
                        dangerouslySetInnerHTML={{
                          __html: getCategoryDescription(item.category),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Advanced Path */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Parcours pour renforcer les connaissances{" "}
                <span className="text-sm text-gray-500">(durée variable)</span>
              </h3>
              <div className="space-y-3">
                {[
                  {
                    text: "Vidéo onglet Documentaires généraux : « traitement des images radar » du CNES",
                    category: "Documentaires généraux",
                  },
                  {
                    text: "Onglet Cours rédigés – lire les chapitres suivants",
                    category: "Cours rédigés",
                  },
                  {
                    text: "Comparer les contenus des cours Plan résumé et liens vers le cours RADAR de l'ESA et Plans resumés et liens vers les cours RADAR, OPTIQUE RADAR, LIDAR de l'ENSG. Faire un bilan, noter les notions difficiles",
                    categories: ["RADAR de l'ESA", "ENSG"],
                  },
                  {
                    text: "Isoler la partie exercices dans le plan du cours ENSG. Evaluer leur difficulté avant de regarder les solutions",
                    category: "",
                  },
                  {
                    text: "Onglet Exposés Power Point. Ecole d'été univ Fiana Avril 2024 – PowerPoint des 2e et 3e jours",
                    category: "Fiana Avril 2024",
                  },
                  {
                    text: "Revoir pour réviser les bases le Power point annoté de Douala",
                    category: "annoté",
                  },
                  {
                    text: "Suivre les cours de l'onglet MOOC multilingue et pdf avec liens actifs EOCollège de l'université JENA (Allemagne)",
                    category: "MOOC multilingue",
                  },
                  {
                    text: "Lire les articles du numéro spécial RADAR dans l'onglet Revue RFPT",
                    category: "Revue RFPT",
                  },
                ].map((item, index) => (
                  <div
                    key={`advanced-${(
                      item.category ||
                      item.text ||
                      item.categories.join("-")
                    )
                      .replace(/\s+/g, "-")
                      .slice(0, 40)}`}
                    className="pl-4 border-l-2 border-green-200"
                  >
                    <p className="text-gray-700">
                      <span className="font-semibold">{index + 1}.</span>{" "}
                      {item.text}
                    </p>
                    {item.category && (
                      <div
                        className="text-xs text-gray-500 mt-1 ml-5"
                        dangerouslySetInnerHTML={{
                          __html: getCategoryDescription(item.category),
                        }}
                      />
                    )}
                    {item.categories && (
                      <div
                        className="text-xs text-gray-500 mt-1 ml-5"
                        dangerouslySetInnerHTML={{
                          __html: item.categories
                            .map((cat) => getCategoryDescription(cat))
                            .filter(Boolean)
                            .join(" • "),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-sm text-center text-gray-500 mt-4 italic">
                Prévoir de discuter avec les autres étudiants tout au long de
                cet approfondissement.
              </p>
            </div>
          </div>
        </div>
      )}

      {books.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            Aucun cours disponible pour le moment.
          </p>
        </div>
      )}
    </div>
  );
}

export default CardBook;
